import { useEffect, useState } from 'react';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { reportCommentDemo, reportFacts } from '@/lib/reportCommentDemo';
import { t, getLanguage } from '@/i18n';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';

// One comment per report and language per page load: the numbers do not change in the demo.
const cache = new Map();

// AI commentary at the top of a report (#72): headline, points, one action.
export const ReportComment = ({ kind, report }) => {
  const key = `${kind}:${getLanguage()}`;
  const [comment, setComment] = useState(cache.get(key) || null);

  useEffect(() => {
    if (cache.has(key)) return undefined;
    let alive = true;
    runAi('report-comment', { kind, lang: getLanguage(), facts: reportFacts(kind, report) }, reportCommentDemo)
      .then(res => {
        cache.set(key, res);
        if (alive) setComment(res);
      });
    return () => { alive = false; };
  }, [key, kind, report]);

  return (
    <div className="rounded-lg bg-primary/5 border border-primary/15 p-3 space-y-2" aria-live="polite">
      <div className="flex items-center gap-2 text-xs font-semibold text-primary">
        <Sparkles className="h-3.5 w-3.5" /> {t('ai.comment.title')}
        {comment && <AiBadge mode={comment.mode} className="ml-auto" />}
      </div>
      {!comment ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> {t('ai.comment.loading')}</div>
      ) : (
        <>
          <p className="text-sm font-medium">{comment.result.headline}</p>
          <ul className="list-disc pl-5 text-sm space-y-0.5">
            {comment.result.points.map(p => <li key={p}>{p}</li>)}
          </ul>
          <p className="text-sm flex items-start gap-1.5"><ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> {comment.result.action}</p>
        </>
      )}
    </div>
  );
};
